import React from 'react'
import { Input as InputComponent } from '@/components/ui/input'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import {
    CAMPAIGN_BUDGET_LIMITS_NOTE,
    CAMPAIGN_BUDGET_SPLIT_NOTE,
    describeLimitsProblem,
    plainCampaignBudgetProblem,
} from '@/utils/campaign-budget'

const Input = InputComponent as React.FC<React.InputHTMLAttributes<HTMLInputElement>>

export interface LimitRow {
    key: string
    name: string
    stage?: string
}

export interface TypedLimits {
    min?: string
    max?: string
}

interface CampaignBudgetSectionProps {
    /** Daily campaign budget as typed (major units). */
    campaignBudget: string
    onBudgetChange: (value: string) => void
    /** Hide the budget box (audiences added to a campaign keep its budget). */
    showBudget?: boolean
    currency?: string
    /** Minimum daily budget of an ad set of the ad account (minor units). */
    minDailyBudget: number
    rows: LimitRow[]
    limits: Record<string, TypedLimits>
    onLimitsChange: (limits: Record<string, TypedLimits>) => void
}

/** Message for a campaign budget the server would refuse, or null. */
export const describePlainBudgetProblem = (
    amount: string,
    adSetCount: number,
    minDailyBudget: number,
    currency?: string,
): string | null => {
    const problem = plainCampaignBudgetProblem(amount, adSetCount, minDailyBudget, currency)
    if (!problem) return null
    if (typeof problem === 'string') return problem
    const count = Math.max(adSetCount, 1)
    return `The campaign budget must be at least ${formatCurrencyAutoLocale({
        amount: problem.minimum / 100,
        currency,
    })} a day, the minimum daily budget of ${count} ${count === 1 ? 'audience' : 'audiences'}.`
}

/** The first problem of the budget and the limits, or null; the same text the section shows. */
export const describeSectionProblem = ({
    campaignBudget,
    showBudget = true,
    minDailyBudget,
    rows,
    limits,
    currency,
}: Pick<
    CampaignBudgetSectionProps,
    'campaignBudget' | 'showBudget' | 'minDailyBudget' | 'rows' | 'limits' | 'currency'
>) =>
    (showBudget ? describePlainBudgetProblem(campaignBudget, rows.length, minDailyBudget, currency) : null) ||
    describeLimitsProblem({
        campaignBudget: showBudget ? campaignBudget : undefined,
        minDailyBudget,
        adSets: rows,
        limits,
        currency,
        formatAmount: (minor: number) => formatCurrencyAutoLocale({ amount: minor / 100, currency }),
    })

/**
 * One daily budget for the whole campaign, shared by Meta across the audiences, with an optional daily minimum and
 * maximum spend for each audience. Placements and audiences stay the owner's.
 */
const CampaignBudgetSection: React.FC<CampaignBudgetSectionProps> = ({
    campaignBudget,
    onBudgetChange,
    showBudget = true,
    currency,
    minDailyBudget,
    rows,
    limits,
    onLimitsChange,
}) => {
    const problem = describeSectionProblem({ campaignBudget, showBudget, minDailyBudget, rows, limits, currency })
    const setLimit = (key: string, field: 'min' | 'max', value: string) =>
        onLimitsChange({ ...limits, [key]: { ...limits[key], [field]: value } })

    return (
        <div className="tw:px-6 tw:flex tw:flex-col tw:gap-3" data-testid="campaign-budget-section">
            {showBudget && (
                <>
                    <label htmlFor="campaign-budget-amount" className="tw:text-sm tw:font-semibold">
                        Daily campaign budget
                    </label>
                    <Input
                        id="campaign-budget-amount"
                        type="text"
                        inputMode="decimal"
                        value={campaignBudget}
                        onChange={(event) => onBudgetChange(event.target.value)}
                        className="tw:max-w-[200px]"
                    />
                </>
            )}
            <h4 className="tw:text-sm tw:font-semibold tw:!m-0">Daily spend limits per audience (optional)</h4>
            <p className="tw:text-sm tw:text-gray-200">{CAMPAIGN_BUDGET_LIMITS_NOTE}</p>
            <table className="tw:text-sm" data-testid="campaign-budget-limits">
                <thead>
                    <tr className="tw:text-left tw:text-gray-200">
                        <th className="tw:pr-4 tw:font-normal">Audience</th>
                        <th className="tw:pr-4 tw:font-normal">Minimum a day</th>
                        <th className="tw:font-normal">Maximum a day</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key}>
                            <td className="tw:pr-4 tw:py-1">{row.name}</td>
                            <td className="tw:pr-4 tw:py-1">
                                <Input
                                    aria-label={`Minimum daily spend of ${row.name}`}
                                    type="text"
                                    inputMode="decimal"
                                    value={limits[row.key]?.min ?? ''}
                                    onChange={(event) => setLimit(row.key, 'min', event.target.value)}
                                    className="tw:w-28"
                                />
                            </td>
                            <td className="tw:py-1">
                                <Input
                                    aria-label={`Maximum daily spend of ${row.name}`}
                                    type="text"
                                    inputMode="decimal"
                                    value={limits[row.key]?.max ?? ''}
                                    onChange={(event) => setLimit(row.key, 'max', event.target.value)}
                                    className="tw:w-28"
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {problem && (
                <span className="tw:text-sm tw:text-yellow-300" role="status" data-testid="campaign-budget-problem">
                    {problem}
                </span>
            )}
            <p className="tw:text-sm tw:text-yellow-300">{CAMPAIGN_BUDGET_SPLIT_NOTE}</p>
        </div>
    )
}

export default CampaignBudgetSection
